import { Check } from 'lucide-react';

import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { useStepper } from './stepper-provider';

interface StepTrackBarProps {
  orientation?: 'vertical' | 'horizontal';
  className?: string;
}

export default function StepVerticalTrackBar({
  orientation = 'vertical',
  className,
}: Readonly<StepTrackBarProps>) {
  const { currentStep, totalSteps, goToStep, completedSteps, getSteps } = useStepper();
  const steps = getSteps();

  if (orientation === 'horizontal') {
    const isActive = (index: number) => currentStep - 1 === index;
    const isCompleted = (index: number) => completedSteps[index];

    return (
      <div className={cn('flex w-full items-center justify-center px-2', className)}>
        {steps.map((step, i) => {
          const active = isActive(i);
          const completed = isCompleted(i);

          return (
            <div key={step.id} className="flex min-w-0 flex-1 items-center last:flex-none">
              <Button
                type="button"
                variant="ghost"
                onClick={() => goToStep(i + 1)}
                aria-label={step.title}
                aria-current={active ? 'step' : undefined}
                className="h-auto shrink-0 rounded-full p-0 hover:bg-transparent"
              >
                <span
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-full border text-base font-bold',
                    active || completed
                      ? 'border-foreground text-foreground'
                      : 'border-muted-foreground/50 text-muted-foreground'
                  )}
                >
                  {completed && !active ? <Check className="h-4 w-4" strokeWidth={2.5} /> : i + 1}
                </span>
              </Button>
              {i < steps.length - 1 ? (
                <div
                  className={cn(
                    'mx-1 h-px min-w-[1.5rem] flex-1 bg-muted-foreground/40',
                    completed && 'bg-foreground/60'
                  )}
                  aria-hidden
                />
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      {steps.map((step, i) => (
        <div
          className={cn('relative flex flex-col [&:not(:last-child)]:flex-1')}
          key={step.id}
          data-completed={completedSteps[i] ? true : false}
        >
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              className={cn(
                'h-8 w-8 rounded-full border p-0 text-lg font-bold text-muted-foreground',
                'data-[current=true]:border-foreground data-[current=true]:text-foreground',
                'data-[completed=true]:border-foreground data-[completed=true]:text-foreground'
              )}
              data-current={currentStep - 1 === i}
              data-completed={completedSteps[i] ? true : false}
              onClick={() => goToStep(i + 1)}
            >
              {completedSteps[i] ? <Check size={18} /> : i + 1}
            </Button>
            <div
              className={cn(
                'ml-4 text-base font-medium text-muted-foreground',
                'data-[current=true]:font-semibold data-[current=true]:text-foreground',
                'data-[completed=true]:text-foreground'
              )}
              data-current={currentStep - 1 === i}
              data-completed={completedSteps[i] ? true : false}
            >
              {step.title}
            </div>
          </div>
          {i !== totalSteps - 1 ? (
            <div
              className={cn(
                'solid-line my-2 ml-4 h-11 w-[1px] bg-muted-foreground',
                'data-[completed=true]:bg-foreground'
              )}
              data-current={currentStep - 1 === i}
              data-completed={completedSteps[i] ? true : false}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}
